// iter-121.117 — OA Cockpit Scope Bar
//
// Sticky bar above the OA Home page. Shows:
//   • Org chip (pinned — OA cannot pick another org)
//   • Trial badge (always visible while on trial; promoted into the
//     action lane when ≤ 3 days remain — server tells us via `header.trial_state`)
//   • Period selector
//   • Cascading filters: Tenant → Workspace → User
//   • "Save as default view"
//
// Header content (greeting + dynamic subtitle + trial state) is sourced
// from `/api/home/oa/header` so EVERY value is wired to a real
// collection — never hardcoded.
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Bookmark, Building2, Calendar, Hourglass, User, Folder } from "lucide-react";
import { toast } from "sonner";
import { useOaScope } from "@/lib/oa-scope";

const PERIOD_OPTIONS = [
    { value: "7d",  label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
    { value: "90d", label: "Last 90 days" },
    { value: "mtd", label: "Month to date" },
    { value: "qtd", label: "Quarter to date" },
    { value: "ytd", label: "Year to date" },
    { value: "custom", label: "Custom range…" },
];

const SENTINEL_ALL = "__all__";

function TrialChip({ trialState }) {
    if (!trialState) return null;
    // ≤ 3d gets promoted to the Action Lane, so we hide the chip there
    // and let the tile carry the urgency.
    if (trialState.in_action_lane) return null;
    const days = trialState.days_remaining;
    const tone = days <= 7 ? "warn" : "info";
    const cls = tone === "warn"
        ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
        : "border-border bg-secondary/40 text-foreground";
    return (
        <div className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-sm border ${cls} text-[11px] font-mono uppercase tracking-wider shrink-0`}
             data-testid="oa-trial-chip"
             data-state={tone}>
            <Hourglass className="h-3.5 w-3.5" />
            Trial · {days} {days === 1 ? "day" : "days"} left
        </div>
    );
}

export default function OaScopeBar({ orgName, dynamicSubtitle, trialState, userName }) {
    const {
        scope, options, optionsLoading, saving,
        setPeriod, setCustomRange, setTenantId, setWorkspaceId, setOwnerId,
        saveAsDefault,
    } = useOaScope();

    const onSave = async () => {
        const ok = await saveAsDefault();
        if (ok) toast.success("Default view saved");
        else toast.error("Failed to save default view");
    };

    const customLabel = (() => {
        if (scope.period !== "custom") return "Custom range";
        const fmt = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "?";
        return `${fmt(scope.custom_start)} → ${fmt(scope.custom_end)}`;
    })();

    const greeting = (() => {
        const h = new Date().getHours();
        const tod = h < 12 ? "morning" : h < 18 ? "afternoon" : "evening";
        return userName ? `Good ${tod}, ${userName}` : "Welcome back";
    })();

    return (
        <div className="border border-border rounded-sm bg-card p-3 mb-4"
             data-testid="oa-scope-bar">
            {/* Top row: greeting + org chip + trial chip + dynamic status */}
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between mb-3">
                <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-2">
                        <div className="text-sm font-medium truncate" data-testid="oa-greeting">
                            {greeting}
                        </div>
                        {orgName && (
                            <div className="inline-flex items-center gap-1.5 px-2 h-6 rounded-sm border border-border bg-secondary/40 text-[11px]"
                                 data-testid="oa-org-chip">
                                <Building2 className="h-3 w-3" />
                                <span className="truncate max-w-[180px]">{orgName}</span>
                            </div>
                        )}
                        <TrialChip trialState={trialState} />
                    </div>
                    {dynamicSubtitle && (
                        <div className="text-xs text-muted-foreground" data-testid="oa-status-subtitle">
                            {dynamicSubtitle}
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom row: scope controls */}
            <div className="flex flex-wrap items-center gap-2">
                <Select value={scope.period} onValueChange={setPeriod}>
                    <SelectTrigger className="h-8 w-[150px] rounded-sm text-[12px]" data-testid="oa-period-select">
                        <Calendar className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                        <SelectValue placeholder="Period" />
                    </SelectTrigger>
                    <SelectContent>
                        {PERIOD_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value} data-testid={`oa-period-${o.value}`}>
                                {o.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                {scope.period === "custom" && (
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" size="sm"
                                    className="h-8 rounded-sm text-[12px] font-normal"
                                    data-testid="oa-custom-range-trigger">
                                <Calendar className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                                {customLabel}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-auto p-0"
                                        data-testid="oa-custom-range-popover">
                            <CalendarPicker
                                mode="range"
                                selected={{
                                    from: scope.custom_start ? new Date(scope.custom_start) : undefined,
                                    to:   scope.custom_end   ? new Date(scope.custom_end)   : undefined,
                                }}
                                onSelect={(range) => {
                                    if (range?.from) {
                                        setCustomRange(
                                            range.from.toISOString().slice(0, 10),
                                            range.to ? range.to.toISOString().slice(0, 10) : range.from.toISOString().slice(0, 10),
                                        );
                                    }
                                }}
                                numberOfMonths={2}
                                initialFocus
                            />
                        </PopoverContent>
                    </Popover>
                )}

                <Select
                    value={scope.tenant_id || SENTINEL_ALL}
                    onValueChange={(v) => setTenantId(v === SENTINEL_ALL ? null : v)}
                    disabled={optionsLoading || options.tenants.length === 0}
                >
                    <SelectTrigger className="h-8 w-[170px] rounded-sm text-[12px]" data-testid="oa-tenant-select">
                        <Building2 className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                        <SelectValue placeholder="All tenants" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={SENTINEL_ALL} data-testid="oa-tenant-all">All tenants</SelectItem>
                        {options.tenants.map((t) => (
                            <SelectItem key={t.id} value={t.id} data-testid={`oa-tenant-${t.id}`}>
                                {t.name}{t.lifecycle === "trial" ? " · trial" : ""}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select
                    value={scope.workspace_id || SENTINEL_ALL}
                    onValueChange={(v) => setWorkspaceId(v === SENTINEL_ALL ? null : v)}
                    disabled={optionsLoading || options.workspaces.length === 0}
                >
                    <SelectTrigger className="h-8 w-[170px] rounded-sm text-[12px]" data-testid="oa-workspace-select">
                        <Folder className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                        <SelectValue placeholder="All workspaces" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={SENTINEL_ALL} data-testid="oa-workspace-all">All workspaces</SelectItem>
                        {options.workspaces.map((w) => (
                            <SelectItem key={w.id} value={w.id} data-testid={`oa-workspace-${w.id}`}>
                                {w.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select
                    value={scope.owner_id || SENTINEL_ALL}
                    onValueChange={(v) => setOwnerId(v === SENTINEL_ALL ? null : v)}
                    disabled={optionsLoading || options.owners.length === 0}
                >
                    <SelectTrigger className="h-8 w-[170px] rounded-sm text-[12px]" data-testid="oa-owner-select">
                        <User className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                        <SelectValue placeholder="All users" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={SENTINEL_ALL} data-testid="oa-owner-all">All users</SelectItem>
                        {options.owners.map((u) => (
                            <SelectItem key={u.id} value={u.id} data-testid={`oa-owner-${u.id}`}>
                                {u.name || u.email}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Button
                    size="sm"
                    variant="outline"
                    className="rounded-sm h-8"
                    onClick={onSave}
                    disabled={saving}
                    data-testid="oa-save-default"
                >
                    <Bookmark className="h-3.5 w-3.5 mr-1.5" />
                    {saving ? "Saving…" : "Save as default"}
                </Button>
            </div>
        </div>
    );
}

// Convenience hook to fetch the OA header data once and share it with
// both the scope bar and the action lane (so trial state is consistent).
export function useOaHeader() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let alive = true;
        api.get("/home/oa/header")
            .then((r) => { if (alive) setData(r.data || null); })
            .catch(() => { if (alive) setData(null); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);
    return { data, loading };
}
